import { Box, Center, Divider, Loader, Stack } from '@mantine/core'
import { useState } from 'react'
import {
  useGetDatasetDetails,
  useGetDatasetVersionsList
} from '@/queries/datasetQueries'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'
import { DatasetInfoSection } from './DatasetInfoSection/DatasetInfoSection'
import { DatasetPreviewDrawer } from './DatasetPreviewDrawer'
import { DatasetPreviewLayout } from './DatasetPreviewLayout'
import { DatasetVersionDetails } from './DatasetVersionDetails'
import { DatasetVersionLinks } from './DatasetVersionLinks/DatasetVersionLinks'

type DatasetPreviewProps = {
  datasetId?: string
  filteredVersionId?: string
  opened: boolean
  onClose: () => void
}

export const DatasetPreview = ({
  datasetId,
  filteredVersionId,
  opened,
  onClose
}: DatasetPreviewProps) => {
  const [selectedVersionId, setSelectedVersionId] = useState('')
  const [isGalleryOpened, setIsGalleryOpened] = useState(false)

  const { data: dataset, isLoading } = useGetDatasetDetails(
    datasetId || '',
    opened && !!datasetId
  )

  const { data: versionsData } = useGetDatasetVersionsList(
    datasetId || '',
    {
      limit: MAX_ITEMS_WITHOUT_PAGINATION,
      ordering: '-created_at'
    },
    {
      enabled: opened && !!datasetId
    }
  )

  const versions = versionsData?.results || []

  const filteredVersions = filteredVersionId
    ? versions.filter((version) => version.id === filteredVersionId)
    : versions

  const activeVersionId = selectedVersionId || filteredVersions[0]?.id

  const handleOnClose = () => {
    setSelectedVersionId('')
    onClose()
  }

  return (
    <>
      <DatasetPreviewDrawer
        opened={opened}
        closeOnEscape={!isGalleryOpened}
        onClose={handleOnClose}
      >
        <DatasetPreviewLayout
          sidebar={
            <>
              {isLoading && (
                <Center mih={100}>
                  <Loader size="sm" />
                </Center>
              )}

              {dataset && (
                <Stack gap="md">
                  <Box p="md">
                    <DatasetInfoSection
                      datasetName={dataset.name}
                      description={dataset.description}
                      labelingType={dataset.labeling_type}
                      datasetType={dataset.dataset_type}
                      createdAt={dataset.created_at}
                      labelingClasses={dataset.labels}
                    />
                  </Box>

                  <Divider />

                  <Box>
                    <DatasetVersionLinks
                      selectedVersionId={activeVersionId || ''}
                      versions={filteredVersions}
                      onVersionSelect={setSelectedVersionId}
                    />
                  </Box>
                </Stack>
              )}
            </>
          }
        >
          <Box py="md" pr="md">
            {activeVersionId && (
              <DatasetVersionDetails
                versionId={activeVersionId}
                onOverlayChange={setIsGalleryOpened}
              />
            )}
          </Box>
        </DatasetPreviewLayout>
      </DatasetPreviewDrawer>
    </>
  )
}
